import React, {useEffect, useRef} from 'react';
import {Mention} from "../types";
import { getEntityTypeDisplay } from "../../../../utils/entityDisplayUtils";
import { HiOutlineBriefcase, HiOutlineUser, HiOutlineUserGroup, HiOutlineViewBoards } from "react-icons/hi";

type SyntheticChangeEvent = {
  target: { value: string; selectionStart?: number };
};

interface MentionTextareaProps {
  value: string;
  onChange?: (e: any) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  wrapperClassName?: string;
  mentions?: Mention[];
  inputRef: React.RefObject<HTMLTextAreaElement>;
  disabled?: boolean;
};

const MentionTextarea: React.FC<MentionTextareaProps> = ({ 
  value, 
  onChange, 
  onKeyDown, 
  placeholder, 
  className = '',
  wrapperClassName = '',
  mentions = [],
  inputRef,
  disabled = false
}) => {
  // The backdrop is a div that sits behind the textarea. It's used to display
  // the text with colorful highlighting for the mentions.
  const backdropRef = useRef<HTMLDivElement | null>(null);

  // Auto-resize textarea based on content
  const autoResize = () => {
    const textarea = inputRef && inputRef.current ? inputRef.current : null;
    if (!textarea) return;

    const MIN_HEIGHT = 50;
    const MAX_HEIGHT = 150;

    const contentValue = typeof textarea.value === 'string' ? textarea.value : '';

    // If no content, reset to minimum height
    if (!contentValue || contentValue.length === 0) {
      textarea.style.height = `${MIN_HEIGHT}px`;
      textarea.style.overflowY = 'hidden';
      if (backdropRef.current) {
        backdropRef.current.style.height = `${MIN_HEIGHT}px`;
      }
      return;
    }

    // Reset height to allow shrink, then set to content height within bounds
    textarea.style.height = 'auto';
    // Force reflow
    // eslint-disable-next-line no-unused-expressions
    // @ts-ignore
    textarea.offsetHeight;

    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(MIN_HEIGHT, Math.min(scrollHeight, MAX_HEIGHT));

    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';

    if (backdropRef.current) {
      backdropRef.current.style.height = `${newHeight}px`;
    }
  };

  // Auto-resize when value changes
  useEffect(() => {
    autoResize();
  }, [value]);

  // Sync scroll between textarea and backdrop
  const handleScroll = () => {
    if (backdropRef.current && inputRef.current) {
      backdropRef.current.scrollTop = inputRef.current.scrollTop;
      backdropRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  };

  // Handle input change with auto-resize
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e);
    }
    // Trigger auto-resize immediately on change
    setTimeout(autoResize, 0);
  };

  // Trigger resize after paste
  const handlePaste = () => {
    // Ensure paste content is committed and layout is updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        autoResize();
      });
    });
  };

  // Trigger resize on any input event (covers typing, cut, etc.)
  const handleInput = () => {
    setTimeout(autoResize, 0);
  };

  // Enhanced key down handler for mention deletion
  const handleKeyDownEnhanced = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.target as HTMLTextAreaElement;
    const cursorPos = textarea.selectionStart || 0;
    const currentValue = textarea.value || '';
    
    // Handle backspace - check if we're about to delete a completed mention
    if (e.key === 'Backspace' && cursorPos > 0) {
      const completedMention = mentions.find(mention => 
        mention.completed && cursorPos === mention.end
      );
      
      if (completedMention) {
        e.preventDefault();
        // Remove the entire mention
        const beforeMention = currentValue.substring(0, completedMention.start);
        const afterMention = currentValue.substring(completedMention.end);
        const newValue = beforeMention + afterMention;
        
        // Create a synthetic event to trigger onChange
        const syntheticEvent: SyntheticChangeEvent = {
          target: { value: newValue, selectionStart: completedMention.start }
        };
        
        if (onChange) {
          onChange(syntheticEvent);
        }
        
        // Set cursor position after deletion
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(completedMention.start, completedMention.start);
          }
        }, 0);
        
        return;
      }
    }
    
    // Handle delete key - check if we're about to delete a completed mention
    if (e.key === 'Delete' && cursorPos < currentValue.length) {
      const completedMention = mentions.find(mention => 
        mention.completed && cursorPos === mention.start
      );
      
      if (completedMention) {
        e.preventDefault();
        // Remove the entire mention
        const beforeMention = currentValue.substring(0, completedMention.start);
        const afterMention = currentValue.substring(completedMention.end);
        const newValue = beforeMention + afterMention;
        
        // Create a synthetic event to trigger onChange
        const syntheticEvent: SyntheticChangeEvent = {
          target: { value: newValue, selectionStart: completedMention.start }
        };
        
        if (onChange) {
          onChange(syntheticEvent);
        }
        
        // Set cursor position after deletion
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(completedMention.start, completedMention.start);
          }
        }, 0);
        
        return;
      }
    }
    
    // Call the original onKeyDown handler for other functionality
    if (onKeyDown) {
      onKeyDown(e);
    }
  };
  
  const renderHighlightedText = () => {
    if (!value) return '' as any;
  
    // If there are no mentions, just return the plain text
    if (mentions.length === 0) {
      return value as any;
    }
  
    const sortedMentions = [...mentions].sort((a, b) => a.start - b.start);
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
  
    sortedMentions.forEach((mention) => {
      // Add the text part before the mention
      if (mention.start > lastIndex) {
        parts.push(value.substring(lastIndex, mention.start));
      }
  
      // Determine the full mention text (with or without @)
      const mentionTextRaw = value[mention.start] === '@' 
        ? value.substring(mention.start, mention.start + mention.name.length + 1)
        : value.substring(mention.start, mention.start + mention.name.length);
      
      
        // Fallback or legacy logic matching AtDropdown
        
      parts.push(
      <span
          key={`${mention.id}-${mention.start}`}
          className="bg-blue-200 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 rounded-md py-0.5"
        >
          {mentionTextRaw}
        </span>
      );

      lastIndex = mention.start + mentionTextRaw.length;
    });

    // Add the remaining text after the last mention
    if (lastIndex < value.length) {
      parts.push(value.substring(lastIndex));
    }

    return parts as any;
  };

  // Check if there are any mentions to highlight
  const hasMentions = mentions && mentions.length > 0;

  return (
    <div
      className={`relative p-2 grid w-full bg-[#f2f4f7] dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-2xl shadow-sm focus-within:ring-offset-transparent transition-colors ${wrapperClassName}`}
    >
      {/* Only render the backdrop if there are mentions to highlight */}
      {hasMentions && (
        <div
          ref={backdropRef}
          className={`${className} w-full whitespace-pre-wrap overflow-hidden  pointer-events-none`}
          style={{
            gridArea: '1 / 1 / 2 / 2',
            minHeight: '50px',
            maxHeight: '150px',
            opacity: 1, // Show backdrop so highlighted mentions are visible
            border: 'none',
            backgroundColor: 'transparent'
          }}
        >
          {renderHighlightedText()}<wbr/>
        </div>
      )}
        <textarea
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDownEnhanced}
          onScroll={handleScroll}
          onPaste={handlePaste}
          onInput={handleInput}
          placeholder={placeholder}
          className={`${className} w-full leading-[22.5px]  dark:bg-transparent  caret-black dark:caret-white outline-none focus:outline-none focus:ring-0 border-none resize-none`}
          rows={1}
          style={{ 
            gridArea: '1 / 1 / 2 / 2', 
            minHeight: '50px', 
            maxHeight: '150px', 
            resize: 'none',
            overflowY: 'auto',
            color: hasMentions ? 'transparent' as any : undefined // Hide duplicate text when highlights are shown
          }}
          disabled={disabled}
        />
    </div>
  );
};

export default MentionTextarea; 


